import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import CarCard from "@/components/CarCard";
import AvailabilitySearch from "@/components/AvailabilitySearch";
import { Seo } from "@/components/Seo";
import { BELO_PROVIDER } from "@/data/cityLocations";
import {
  Plane, ShieldCheck, Clock, MapPin, ChevronDown, Phone, Sparkles, ArrowRight,
} from "lucide-react";

const SITE = "https://belorentals.com";
const HERO_IMG = "/marketing/logan-airport.webp";

const STEPS = [
  { icon: Plane, title: "Share your flight details", body: "Book online and tell us your Logan Airport (BOS) arrival time so we can plan a smooth, on-time handoff." },
  { icon: MapPin, title: "Meet at the airport", body: "Add our Logan Airport pickup & drop-off at checkout and skip the rental-counter lines — a managed, professional handoff." },
  { icon: Sparkles, title: "Hit the road", body: "Drive away in a clean, inspected, well-maintained car and enjoy your trip on your own schedule." },
];

const FAQS = [
  { q: "How far is Belo Rentals from Logan Airport (BOS)?", a: "Our Malden location is about 7 miles from Boston Logan, and we offer Logan Airport pickup and drop-off so you can skip the rental-counter lines." },
  { q: "Do you pick up and drop off at Logan Airport?", a: "Yes. Belo Rentals hosts who opt in offer Logan Airport pickup and return. Choose a host who offers the service, book on their Belo Rentals profile, and add airport pickup & return at checkout." },
  { q: "How much does it cost to rent a car near Logan Airport?", a: "Rentals start from $50/day. Because Belo is also a marketplace, vetted local hosts set their own competitive rates, so you'll find a range of cars and price points. Airport pickup and return is an optional add-on set by each host." },
  { q: "How do I book the Logan Airport pickup & return?", a: "Visit our Logan Airport pickup & return page to see hosts who offer the service. Pick a host, open their Belo Rentals profile, choose your vehicle and dates, and select 'Logan Airport pickup & return' at checkout." },
  { q: "What do I need to rent a car?", a: "A valid driver's license and a credit card. You must meet the minimum rental age — a young-driver fee may apply for drivers under 25." },
  { q: "How does protection work?", a: "Protection details are shown before you pay — see your rental agreement for exact coverage specifics, so there are no surprises." },
  { q: "Can I book online?", a: "Absolutely. Pick your dates, choose a car, e-sign the rental agreement, and pay securely online — it takes just a few minutes." },
  { q: "Do you serve areas beyond Boston?", a: "We're based in Malden (near Logan) and Clinton, MA, and serve travelers and locals throughout Greater Boston." },
];

export default function LoganAirport() {
  const [cars, setCars] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/cars").then((r) => setCars((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Logan Airport Car Rental", item: `${SITE}/logan-airport-car-rental` },
    ],
  };
  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Airport Car Rental",
    name: "Logan Airport Car Rental",
    areaServed: { "@type": "City", name: "Boston, MA" },
    provider: { ...BELO_PROVIDER, image: `${SITE}${HERO_IMG}` },
  };

  return (
    <div data-testid="logan-airport-page" className="bg-white">
      <Seo
        title="Logan Airport Car Rental | Belo Rentals Boston Airport Pickup"
        description="Renting a car near Logan Airport? Belo Rentals offers Logan Airport (BOS) pickup & drop-off, a professionally maintained fleet plus vetted local hosts, from $50/day. Book online."
        path="/logan-airport-car-rental"
        keywords="car rental near Logan Airport, Logan Airport car rental, Boston airport car rental, car rental Boston Logan, Turo alternative Boston, airport car rental drop off Boston, BOS car rental"
        image={`${SITE}${HERO_IMG}`}
        jsonLd={[faqJsonLd, breadcrumbLd, serviceLd]}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-5 sm:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-600/10 text-blue-700 text-sm font-medium mb-6">
            <Plane size={15} /> Logan Airport pickup &amp; drop-off available
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
            Car Rental Near Logan Airport, Boston
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            Land at Boston Logan (BOS) and skip the counter lines. Belo Rentals gets you into a clean, inspected,
            well-maintained car — from <span className="font-semibold text-slate-800">$50/day</span> — with optional
            Logan Airport pickup &amp; return from hosts who offer it.
          </p>
          <div className="mt-9 max-w-2xl mx-auto">
            <AvailabilitySearch />
            <p className="mt-4 text-sm text-slate-500 flex items-center justify-center gap-1.5">
              <Phone size={14} className="text-slate-400" /> Prefer to talk? Call{" "}
              <a data-testid="logan-cta-call" href="tel:+19782211451" className="text-blue-600 font-semibold hover:underline">(978) 221-1451</a>
            </p>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-4">
        <img
          src={HERO_IMG}
          alt="Car rental near Boston Logan Airport (BOS) — Belo Rentals"
          loading="eager"
          className="w-full aspect-[21/9] object-cover rounded-3xl shadow-card"
          data-testid="logan-hero-image"
        />
      </div>

      {/* How it works */}
      <section className="py-20 px-5 sm:px-8 max-w-6xl mx-auto">
        <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">How Logan Airport pickup works</h2>
        <p className="text-slate-500 text-center mt-3 max-w-2xl mx-auto">Three easy steps from landing to driving.</p>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {STEPS.map((s, i) => (
            <div key={i} className="bg-slate-50 rounded-2xl p-7 border border-slate-100">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-blue-600 text-white mb-5"><s.icon size={22} /></span>
              <div className="text-xs font-bold text-blue-600 mb-1">STEP {i + 1}</div>
              <h3 className="font-heading text-lg font-bold text-slate-900">{s.title}</h3>
              <p className="text-slate-600 text-sm mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 rounded-2xl border border-blue-100 bg-blue-50/60 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="pickup-directions-callout">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white shrink-0"><MapPin size={20} /></span>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-slate-900">Flying in? See step-by-step pickup directions</h3>
            <p className="text-slate-600 text-sm mt-1">Terminal-by-terminal shuttle guidance to the Logan Economy Parking Garage car-share pickup area, plus a return timeline.</p>
          </div>
          <Link to="/logan-airport-car-rental-pickup-directions" data-testid="logan-pickup-directions-link" className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition-all shrink-0">
            Pickup directions <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Airport pickup & return */}
      <section className="py-20 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-5xl mx-auto rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <span className="grid place-items-center w-12 h-12 rounded-xl bg-blue-600 text-white shrink-0"><Plane size={22} /></span>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-bold text-slate-900">Need a Logan Airport handoff?</h2>
            <p className="text-slate-600 text-sm mt-1">
              See Belo Rentals hosts who offer Logan Airport (BOS) pickup &amp; return — compare their vehicles, ratings and airport fee, then book directly on their profile.
            </p>
          </div>
          <Link to="/logan-airport-drop-off" data-testid="logan-airport-dropoff-link" className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition-all shrink-0">
            Airport hosts <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-12 px-5 sm:px-8 max-w-5xl mx-auto grid sm:grid-cols-3 gap-6 text-center">
        {[
          { icon: ShieldCheck, t: "Transparent protection", s: "Details shown before you pay." },
          { icon: Clock, t: "No host wait", s: "Fast, managed handoffs." },
          { icon: Plane, t: "Logan made easy", s: "Airport pickup & drop-off available." },
        ].map((b, i) => (
          <div key={i} className="flex flex-col items-center">
            <span className="grid place-items-center w-12 h-12 rounded-full bg-blue-50 text-blue-600 mb-3"><b.icon size={22} /></span>
            <div className="font-heading font-bold text-slate-900">{b.t}</div>
            <div className="text-sm text-slate-500">{b.s}</div>
          </div>
        ))}
      </section>

      {/* Fleet preview */}
      {cars.length > 0 && (
        <section className="py-16 px-5 sm:px-8 max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-heading text-3xl font-bold text-slate-900">Popular cars for your Boston trip</h2>
            <Link to="/fleet" className="text-blue-600 font-semibold text-sm hover:underline">View all →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cars.map((car, i) => <CarCard key={car.id} car={car} index={i} />)}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">Logan Airport car rental FAQ</h2>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} data-testid={`faq-item-${i}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-medium text-slate-900">{f.q}</span>
                  <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-r from-slate-900 to-blue-900 text-white text-center px-8 py-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">Flying into Boston Logan?</h2>
          <p className="mt-4 text-blue-100 max-w-xl mx-auto">Reserve your car now, with optional Logan Airport pickup &amp; drop-off. From $50/day.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/fleet" className="px-7 py-3.5 rounded-full bg-white text-slate-900 font-semibold hover:bg-blue-50 transition-colors">Book your car</Link>
            <Link to="/contact" className="px-7 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">Contact us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
